<template>
  <v-container fluid>
    <el-overlay-loading :loading="pageloading" />
    <h3 ref="topObjektas" class="mb-5">{{ tekstas.title }}</h3>

    <v-data-table
      :loading="loading"
      :headers="filteredHeaders"
      :items="itemsList"
      :search="search"
      :item-class="rowClass"
      :footer-props="{
        showFirstLastPage: true,
        showCurrentPage: true,
        itemsPerPageText: 'Rodyti po:',
        itemsPerPageAllText: 'visi',
        itemsPerPageOptions: itemsPerPageOptions,
      }"
      :hide-default-footer="hideFooter"
      :items-per-page="pageSize"
    >
      <template #top>
        <v-toolbar flat>
          <v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            label=""
            single-line
            hide-details
          ></v-text-field>
          <v-spacer></v-spacer>
          <el-button-prideti
            v-if="!isReadonlyForm"
            primary
            @click.native.stop="newItem()"
          >
            {{ tekstas.add }}
          </el-button-prideti>
        </v-toolbar>
      </template>
      <template #[`item.pri_pavadinimas`]="{ item }">
        <div class="text-cut-4">
          {{ item.pri_pavadinimas }}
        </div>
      </template>
      <template #[`item.pri_terminas_tekstas`]="{ item }">
        <div class="text-cut-4">
          {{ item.pri_terminas_tekstas }}
        </div>
      </template>
      <template #[`item.actions`]="{ item }">
        <el-table-action
          :item="item"
          @editItem="editItem(item)"
          @viewItem="editItem(item)"
          @deleteItem="deleteItem(item)"
          @restoreItem="restoreItem(item)"
        />
      </template>
      <template slot="loading">
        Kraunasi...
      </template>
      <template slot="no-results">
        Duomenų nėra
      </template>
      <template slot="no-data">
        Duomenų nėra
      </template>
      <template #[`footer.page-text`]="props">
        Rodoma {{ props.pageStart }}-{{ props.pageStop }} iš
        {{ props.itemsLength }}
      </template>
    </v-data-table>

    <div ref="detailObjektas"></div>
    <template v-if="showPrievoleObjektas">
      <v-divider class="mt-5" />
      <prievole-objektas
        :key="prievoleComponentKey"
        @closePrievole="closePrievole()"
        :labels="labels"
        :valiutos="valiutos"
        :readonly="isReadonlyForm"
      />
    </template>

    <template v-if="!showPrievoleObjektas">
      <hipoteka-istorija class="mt-4" duomenys="prievole" />

      <v-footer class="form-footer">
        <v-spacer></v-spacer>
        <el-button
          v-if="!isReadonlyForm"
          primary
          @click.native="checkErrors()"
          :loading="loading"
          >Tikrinti</el-button
        >
      </v-footer>
    </template>

    <modal-delete
      title="Šalinti prievolę?"
      :text="
        `Ar norite pašalinti prievolę <strong>${modalDeleteVardas}<strong>?`
      "
      v-model="showDelete"
      :item="modalDeleteItem"
      @confirmDelete="confirmDelete"
    />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import HipotekaMap from './../hipotekaMap'
import ModalDelete from '../../modals/ModalDelete'
import PrievoleObjektas from './PrievoleObjektas'
import checkErrors from '../../../mixins/checkErrors'
import math from '../../../mixins/math'
import rowClass from '../../../mixins/rowClass'
import HipotekaIstorija from './../istorija/HipotekaIstorija'

export default {
  name: 'HipotekaPrievole',
  components: {
    PrievoleObjektas,
    ModalDelete,
    HipotekaIstorija,
  },
  mixins: [checkErrors, math, rowClass],
  props: {
    readonlyForm: { type: Boolean, default: false },
  },
  data() {
    return {
      pageloading: true,
      loading: true,
      search: null,
      prievoleItem: {},
      showDelete: false,
      modalDeleteVardas: '',
      modalDeleteItem: null,
      prievoles: [],
      obj_rusys: {},
      valiutos: [],
      prievoleRefresh: 0,
      loadItem: {},
      itemsPerPageOptions: [10, 50, -1],
      pageSize: 10,
      testas: null,
    }
  },
  computed: {
    ...mapGetters(['objektasID', 'hipotekaTipas', 'hipotekaReadonly']),
    isReadonlyForm() {
      return this.hipotekaReadonly || this.readonlyForm
    },
    headers() {
      return [
        {
          text: this.tekstas.tablePrievole,
          align: 'start',
          value: 'pri_pavadinimas',
        },
        {
          text: this.tekstas.tableDydis,
          align: 'start',
          value: 'pri_dydis',
        },
        {
          text: 'Skolos dydžio fiksavimo data',
          align: 'start',
          value: 'pri_skolos_data',
          hide: !this.isMaksimalus,
        },
        {
          text: 'Įvykdymo terminas',
          align: 'start',
          value: 'pri_terminas_tekstas',
        },
        {
          text: 'Veiksmai',
          align: 'start',
          sortable: false,
          value: 'actions',
        },
      ]
    },
    filteredHeaders() {
      return this.headers.filter(item => {
        return !item.hide
      })
    },
    itemsList() {
      return this.prievoles.map(prievole => {
        prievole.pri_dydis = this.moneyformat(prievole.pri_dydis)
        prievole.pri_dydis += ` ${prievole.pri_valiuta}`
        if (prievole.pri_skolos_data_nenustatoma) {
          prievole.pri_skolos_data = 'Nenustatoma'
        }

        if (!this.isReadonlyForm) {
          prievole.canEdit = !prievole.deleted
          prievole.canDelete = !prievole.deleted
          prievole.canRestore =
            prievole.tevinis_id > 0 && (prievole.deleted || prievole.edited)
        } else {
          prievole.canView = true
        }
        return prievole
      })
    },
    hideFooter() {
      return this.itemsList.length <= 10
    },
    tekstas() {
      const key = Object.keys(HipotekaMap['prievoleTekstas']).find(e =>
        this.hipotekaTipas.includes(e)
      )
      return key ? HipotekaMap['prievoleTekstas'][key] : {}
    },
    labels() {
      const tekstasKey = Object.keys(HipotekaMap['prievoleTekstas']).find(e =>
        this.hipotekaTipas.includes(e)
      )
      const labelKey = this.obj_rusys.maksimalus ? 'maksimalus' : 'paprastas'
      return HipotekaMap['prievoleTekstas'][tekstasKey].labels[labelKey]
    },
    prievoleComponentIndex() {
      return this.$route.params.prievoleID > 0
        ? this.$route.params.prievoleID
        : this.prievoleRefresh
    },
    prievoleComponentKey() {
      return `prievole-${this.prievoleComponentIndex}`
    },
    showPrievoleObjektas() {
      return this.$route.name === 'prievoleObjektas'
    },
    isMaksimalus() {
      return this.obj_rusys.maksimalus
    },
    isPriverstinis() {
      return this.hipotekaTipas.includes('priverstinis')
    },
  },
  beforeCreate() {
    this.$root.$off('updatePrievoleList')
  },
  created() {
    this.$root.$on('updatePrievoleList', () => {
      this.getData()
    })
    this.getData(true)
  },
  methods: {
    getData() {
      this.loading = true
      this.$axios
        .get(`objektas/${this.objektasID}/prievole`)
        .then(res => {
          this.prievoles = res.data.prievoles
          this.obj_rusys = res.data.obj_rusys
          this.valiutos = res.data.valiutos
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          this.loading = false
          this.pageloading = false
        })
    },
    newItem() {
      this.loadItem = { pri_id: 0 }
      this.$root.$emit('checkDataChangedprievoleObjektas')
      this.prievoleRefresh++
      this.openPrievole()
    },
    editItem(item) {
      if (this.$route.params.prievoleID !== item.pri_id) {
        this.loadItem = item
        this.openPrievole()
      }
    },
    openPrievole() {
      const item = this.loadItem
      this.$router
        .push({
          name: 'prievoleObjektas',
          params: {
            prievoleID: item.pri_id,
          },
        })
        .then(this.$vuetify.goTo(this.$refs.detailObjektas))
    },
    closePrievole() {
      this.$vuetify.goTo(this.$refs.topObjektas)
      this.$router.push({
        name: 'hipotekaPrievole',
        params: {
          objektasID: this.objektasID,
        },
      })
    },
    deleteItem(item) {
      this.modalDeleteVardas = item.pavadinimasCut
      this.modalDeleteItem = Object.assign({}, item)
      this.showDelete = true
    },
    confirmDelete(item) {
      this.$axios
        .delete(`objektas/${this.objektasID}/prievole/${item.pri_id}`)
        .then(res => {
          this.getData()
          window._VMA.$emit('DATA_OK', res.data.message)
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          if (this.$route.name !== 'hipotekaPrievole') {
            this.$router.push({
              name: 'hipotekaPrievole',
              params: {
                objektasID: this.objektasID,
              },
            })
          }
        })
    },
    restoreItem(item) {
      if (item.pri_id === this.prievoleItem.pri_id) {
        this.showPrievole = false
      }
      this.$axios
        .post(`objektas/${this.objektasID}/prievole/${item.pri_id}/restore`)
        .then(res => {
          this.getData()
          window._VMA.$emit('DATA_OK', res.data.message)
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
    },
    getActiveClass(item) {
      return this.$route.params.prievoleID &&
        item.pri_id === this.$route.params.prievoleID
        ? 'row-active'
        : ''
    },
  },
}
</script>
